// Startup programme (gateway src/startups/): the founder's application form,
// the programme officer's review and the admin controls.
import { apiClient } from '@/lib/api';

export interface Cohort { id: string; name: string; opens_at: number; closes_at: number }

export interface StartupMeta {
  cohort: Cohort | null;
  next: Cohort | null;
  countries: { code: string; name: string; registry_label: string }[];
  accelerators: { id: string; name: string }[];
  application: { id: string; status: 'waiting' | 'decided' } | null;
}

export interface AddressResult { label: string; lat: number; lon: number }

export interface ApplicationBody {
  business_name: string;
  reg_country: string;
  reg_country_other?: string;
  reg_number: string;
  address: { text: string; lat?: number; lon?: number; label?: string };
  company_domain: string;
  product_domain?: string;
  founder_name: string;
  founder_linkedin: string;
  cofounder_linkedins: string[];
  customer_countries: string[];
  stage: 'live' | 'mvp' | 'building';
  product_link?: string;
  mvp_plan?: string;
  pitch: string;
  accelerator: { id: string } | { other: string } | { none: true };
  accelerator_year?: number;
}

interface PhotonFeature { geometry: { coordinates: [number, number] }; properties: Record<string, string | undefined> }

/** "Westlands Road, Westlands, Nairobi, Kenya" from a geocoder result. */
const featureLabel = (p: Record<string, string | undefined>) =>
  [p.name, [p.housenumber, p.street].filter(Boolean).join(' '), p.district ?? p.locality, p.city, p.state, p.country]
    .filter((v, i, all) => v && all.indexOf(v) === i).join(', ');

export const startupsApi = {
  meta: () => apiClient.get<StartupMeta>('/startups/meta').then(r => r.data),
  sendCode: (domains: { company_domain: string; product_domain?: string }) =>
    apiClient.post<{ sent: true; expires_in: number }>('/startups/email-code', domains).then(r => r.data),
  verifyCode: (code: string) => apiClient.post<{ proved: true }>('/startups/email-code/verify', { code }).then(r => r.data),
  searchAddress: (q: string) =>
    apiClient.get<{ features: PhotonFeature[] }>('/startups/address', { params: { q } })
      .then(r => r.data.features.map((f): AddressResult => ({
        label: featureLabel(f.properties), lon: f.geometry.coordinates[0], lat: f.geometry.coordinates[1],
      }))),
  submit: (app: ApplicationBody, turnstileToken: string | null) =>
    apiClient.post<{ id: string }>('/startups/applications', { ...app, turnstileToken: turnstileToken ?? undefined }).then(r => r.data),
};

/** "15 January 2028" */
export const formatDay = (s: number) =>
  new Date(s * 1000).toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric', timeZone: 'UTC' });

export type CheckResult = { ok: boolean | null; detail: string };
export type CheckKey = 'email' | 'site' | 'domainAge' | 'ranking' | 'address' | 'accelerator' | 'duplicate';
export type Status = 'waiting' | 'approved' | 'rejected';

export interface ProgrammeCohort extends Cohort { waiting: number; approved: number; rejected: number }
export interface ApplicationRow {
  id: string; business_name: string; company_domain: string; product_domain: string | null; reg_country: string;
  status: Status; created_at: number; checks_summary: { ok: number; total: number } | null;
}
export interface ApplicationDetail {
  id: string; status: Status; business_name: string; cohort_name: string; email: string; created_at: number;
  reg_country: string; reg_number: string; address_text: string; address_label: string | null; address_lat: number | null; address_lon: number | null;
  company_domain: string; product_domain: string | null; founder_name: string; founder_linkedin: string; cofounder_linkedins: string[] | null;
  customer_countries: string[]; stage: 'live' | 'mvp' | 'building'; product_link: string | null; mvp_plan: string | null; pitch: string;
  accelerator_name: string | null; accelerator_other: string | null; accelerator_year: number | null;
  reject_reason: string | null; reject_note: string | null; decided_by_email: string | null;
  checks: Partial<Record<CheckKey, CheckResult>> | null;
}

export const REJECT_REASONS: { id: string; label: string }[] = [
  { id: 'not_africa', label: 'Not Africa-focused or no African customers' },
  { id: 'traffic', label: 'Not enough traffic' },
  { id: 'accelerator', label: 'Accelerator unknown or none' },
  { id: 'no_mvp', label: 'No MVP or path to one' },
  { id: 'fake_address', label: 'Fake address' },
  { id: 'false_claim', label: 'False business claim' },
  { id: 'other', label: 'Other' },
];

export const programmeApi = {
  cohorts: () => apiClient.get<{ cohorts: ProgrammeCohort[] }>('/programme/cohorts').then(r => r.data),
  list: (cohort: string) => apiClient.get<{ applications: ApplicationRow[] }>('/programme/applications', { params: { cohort } }).then(r => r.data),
  get: (id: string) => apiClient.get<ApplicationDetail>(`/programme/applications/${encodeURIComponent(id)}`).then(r => r.data),
  approve: (id: string) => apiClient.post(`/programme/applications/${encodeURIComponent(id)}/approve`).then(r => r.data),
  reject: (id: string, reason: string, note: string) =>
    apiClient.post(`/programme/applications/${encodeURIComponent(id)}/reject`, { reason, note: note || undefined }).then(r => r.data),
  reopen: (id: string) => apiClient.post(`/programme/applications/${encodeURIComponent(id)}/reopen`).then(r => r.data),
};

export interface Officer { user_id: string; email: string; full_name: string | null }
export interface Accelerator { id: string; name: string; active: number }

export const startupsAdminApi = {
  cohorts: () => apiClient.get<{ cohorts: Cohort[] }>('/admin/startups/cohorts').then(r => r.data),
  saveCohort: (c: Omit<Cohort, 'id'>, id?: string) =>
    (id ? apiClient.put(`/admin/startups/cohorts/${encodeURIComponent(id)}`, c) : apiClient.post('/admin/startups/cohorts', c)).then(r => r.data),
  accelerators: () => apiClient.get<{ accelerators: Accelerator[] }>('/admin/startups/accelerators').then(r => r.data),
  addAccelerator: (name: string) => apiClient.post('/admin/startups/accelerators', { name }).then(r => r.data),
  setAccelerator: (id: string, active: boolean) => apiClient.put(`/admin/startups/accelerators/${encodeURIComponent(id)}`, { active }).then(r => r.data),
  officers: () => apiClient.get<{ officers: Officer[] }>('/admin/startups/officers').then(r => r.data),
  addOfficer: (email: string) => apiClient.post('/admin/startups/officers', { email }).then(r => r.data),
  removeOfficer: (userId: string) => apiClient.delete(`/admin/startups/officers/${encodeURIComponent(userId)}`).then(r => r.data),
};
